import { AxiosInstance } from "axios"

import {
  DatasetSource,
  DatasetUpdatePayload,
  TradingDataset,
} from "../views/pages/Config/Dataset/types"
import { CompanyUpdatePayload } from "../views/pages/Config/Company/types"
import {
  Country,
  Currency,
  InstrumentDetail,
  InstrumentLight,
  InstrumentUpdatePayload,
  Region,
  Sector,
  TradingCompany,
  TradingCompanyDetail,
} from "../views/pages/Config/types"
import {
  IRefreshTradingDataResponse,
  ITradingData,
  ITradingSummary,
} from "../views/pages/PortfolioManagement/Portfolio/types"
import {
  IFactorModels,
  IRiskExposures,
  IRiskKeyMetrics,
  RiskZoom,
} from "../views/pages/PortfolioManagement/Risks/types"
import { ComplianceRules } from "../views/pages/PortfolioManagement/Trading/types/compliance"
import { Order } from "../views/pages/PortfolioManagement/Trading/types/order"
import { Wave } from "../views/pages/PortfolioManagement/Trading/types/wave"
import { ScientTradingAccount, TradingBook } from "../views/pages/PortfolioManagement/types"
import {
  ShortLocatePayload,
  ShortLocateResponse,
  ShortLocateListResponse,
} from "../views/pages/PortfolioManagement/Trading/types/common"
import { TradingDatasetLight } from "../types/trading"

const WAVES_PER_PAGE = 15

const DATASETS_PER_PAGE = 100

export enum DatasetOrdering {
  NAME = "name",
  LONG_NAME = "long_name",
  FUTURE_RELEASE_DATE = "future_release_date",
}

export enum DatasetSorting {
  DESC = "DESC",
  ASC = "ASC",
}

export interface CSVUploadResponse {
  id: number
  status: string
  message: string
}

export interface ITradingAPI {
  getBooks: ({ accountIds }: { accountIds?: number[] }) => Promise<TradingBook[]>
  getTradingAccountsScient: () => Promise<ScientTradingAccount[]>
  getTradingSummary: ({ liveOrders }: { liveOrders: boolean }) => Promise<ITradingSummary>
  getTradingData: ({
    accountBookPairs,
    liveOrders,
    withPnl,
    useAccountCurrency,
  }: {
    accountBookPairs?: Array<{ account_id: number; book_ids?: number[] }>
    liveOrders: boolean
    withPnl?: boolean
    useAccountCurrency?: boolean
  }) => Promise<ITradingData>
  refreshTradingData: () => Promise<IRefreshTradingDataResponse>
  uploadPositionsCSV: (file: File, overwrite?: boolean) => Promise<CSVUploadResponse>
  uploadDatasetsCSV: (file: File) => Promise<CSVUploadResponse>
  cancelOrder: ({ orderId }: { orderId: string }) => Promise<{
    success: boolean
    status_code: string
    description: string
  }>
  getRiskKeyMetrics: ({
    accountBookPairs,
  }: {
    accountBookPairs?: Array<{ account_id: number; book_ids?: number[] }>
  }) => Promise<IRiskKeyMetrics>
  getRiskExposures: ({
    accountBookPairs,
    type,
  }: {
    accountBookPairs?: Array<{ account_id: number; book_ids?: number[] }>
    type: string
  }) => Promise<IRiskExposures>
  getRiskZoom: ({
    accountBookPairs,
    type,
    category,
  }: {
    type: string
    accountBookPairs?: Array<{ account_id: number; book_ids?: number[] }>
    category?: string
  }) => Promise<RiskZoom>
  getFactorModels: () => Promise<IFactorModels>
  getTradeWaves: (pageParam?: string) => Promise<{
    data: Wave[]
    next_cursor: string | null
  }>
  createTradeWave: (values: Partial<Wave>) => Promise<Wave>
  getOrder: (orderId: string) => Promise<Order>
  replaceOrder: (orderId: string, values: Partial<Order>) => Promise<Order>
  getComplianceRules: () => Promise<ComplianceRules>
  getTradingCurrencies: () => Promise<Currency[]>
  getTradingCountries: () => Promise<Country[]>
  getTradingSectors: () => Promise<Sector[]>
  getTradingCompanies: ({ query }: { query?: string }) => Promise<TradingCompany[]>
  getTradingCompany: (companyId: number) => Promise<TradingCompanyDetail>
  getTradingRegions: () => Promise<Region[]>
  updateTradingCompany: ({
    companyId,
    company,
  }: {
    companyId: number
    company: CompanyUpdatePayload
  }) => Promise<TradingCompanyDetail>
  getTradingDatasets: ({
    pageParam,
    codeStartswithParam,
    nameParam,
    longNameParam,
    sourceParam,
    currencyCodeParam,
    countryCodeParam,
    regionIdParam,
    subRegionIdParam,
    sectorsParam,
    withPaginationParam,
    orderBy,
    sortDirection,
  }: {
    pageParam?: string
    codeStartswithParam?: string
    nameParam?: string
    longNameParam?: string
    sourceParam?: DatasetSource
    currencyCodeParam?: string[]
    countryCodeParam?: string[]
    regionIdParam?: number
    subRegionIdParam?: number
    sectorsParam?: number[]
    withPaginationParam?: boolean
    orderBy?: DatasetOrdering
    sortDirection?: DatasetSorting
  }) => Promise<{
    data: TradingDataset[]
    next_cursor: string | null
  }>
  getTradingDataset: (datasetId: string) => Promise<TradingDataset>
  getTradingInstruments: ({
    query,
    withPriceUsd,
  }: {
    query: string
    withPriceUsd?: boolean
  }) => Promise<InstrumentLight[]>
  getTradingInstrument: (instrumentId: number) => Promise<InstrumentDetail>
  updateTradingInstrument: ({
    instrumentId,
    instrument,
  }: {
    instrumentId: number
    instrument: InstrumentUpdatePayload
  }) => Promise<InstrumentDetail>
  updateTradingDataset: ({
    datasetId,
    dataset,
  }: {
    datasetId: number
    dataset: DatasetUpdatePayload
  }) => Promise<TradingDataset>
  createDataset: (dataset: { name: string; scient_code: string }) => Promise<TradingDataset>
  getShortLocates: () => Promise<ShortLocateListResponse>
  createManualShortLocate: (payload: ShortLocatePayload) => Promise<ShortLocateResponse>
  searchDatasets: (searchTerm: string) => Promise<TradingDatasetLight[]>
}

const trading = (axiosClient: AxiosInstance): ITradingAPI => ({
  getBooks: async ({ accountIds }: { accountIds?: number[] }) => {
    const { data } = await axiosClient.get<TradingBook[]>("/trading/books", {
      params: { account_id: accountIds },
    })

    return data
  },
  getTradingAccountsScient: async () => {
    const { data } = await axiosClient.get<ScientTradingAccount[]>("/trading/accounts")

    return data
  },
  getTradingSummary: async ({ liveOrders }: { liveOrders: boolean }) => {
    const { data } = await axiosClient.get<ITradingSummary>("/trading/summary", {
      params: { live_orders: liveOrders, use_legacy_format: false },
    })

    return data
  },
  getTradingData: async ({
    accountBookPairs,
    liveOrders,
    withPnl = true,
    useAccountCurrency = false,
  }: {
    accountBookPairs: Array<{ account_id: number; book_ids?: number[] }>
    liveOrders: boolean
    withPnl?: boolean
    useAccountCurrency?: boolean
  }) => {
    const { data } = await axiosClient.get<ITradingData>("/trading", {
      params: {
        account_book_pairs: JSON.stringify(accountBookPairs),
        live_orders: liveOrders,
        use_legacy_format: false,
        with_pnl: withPnl,
        use_account_currency: useAccountCurrency,
      },
    })

    return data
  },
  /**
   * POST /trading/refresh/
   */
  refreshTradingData: async () => {
    const { data } = await axiosClient.post<IRefreshTradingDataResponse>("/trading/refresh/")

    return data
  },
  getRiskKeyMetrics: async ({
    accountBookPairs,
  }: {
    accountBookPairs: Array<{ account_id: number; book_ids?: number[] }>
  }) => {
    const { data } = await axiosClient.get<IRiskKeyMetrics>("/trading/risk/key-metrics", {
      params: { account_book_pairs: JSON.stringify(accountBookPairs) },
    })

    return data
  },
  getRiskExposures: async ({
    accountBookPairs,
    type,
  }: {
    accountBookPairs: Array<{ account_id: number; book_ids?: number[] }>
    type: string
  }) => {
    const { data } = await axiosClient.get<IRiskExposures>("/trading/risk/exposures", {
      params: { account_book_pairs: JSON.stringify(accountBookPairs), type },
    })

    return data
  },
  getRiskZoom: async ({
    accountBookPairs,
    type,
    category,
  }: {
    accountBookPairs: Array<{ account_id: number; book_ids?: number[] }>
    type: string
    category: string
  }) => {
    const { data } = await axiosClient.get<RiskZoom>("/trading/risk/zoom", {
      params: { account_book_pairs: JSON.stringify(accountBookPairs), type, category },
    })

    return data
  },
  getFactorModels: async () => {
    const { data } = await axiosClient.get<IFactorModels>("/trading/factor-models/")

    return data
  },
  /**
   * GET /trading/waves/
   */
  getTradeWaves: async (pageParam: string) => {
    const { data } = await axiosClient.get<{ data: Wave[]; next_cursor: string }>(
      `/trading/waves/`,
      {
        params: {
          per_page: WAVES_PER_PAGE,
          next_cursor: pageParam,
        },
      },
    )

    return data
  },
  /**
   * POST /waves/
   */
  createTradeWave: async (values: Partial<Wave>) => {
    const { data } = await axiosClient.post<Wave>("/trading/waves/", values)

    return data
  },

  /**
   * GET /trading/orders/<order_id>/
   */
  getOrder: async (orderId: string) => {
    const { data } = await axiosClient.get<Order>(`/trading/orders/${orderId}/`)
    return data
  },

  /**
   * POST /trading/orders/<order_id>/replace/
   */
  replaceOrder: async (orderId: string, values: Partial<Order>) => {
    const { data } = await axiosClient.post<Order>(`/trading/orders/${orderId}/replace/`, values)

    return data
  },

  /**
   * GET /trading/compliance/rules/
   */
  getComplianceRules: async () => {
    const { data } = await axiosClient.get<ComplianceRules>("/trading/compliance/rules/")

    return data
  },

  /**
   * GET /api/v1/trading/currencies/
   */
  getTradingCurrencies: async () => {
    const { data } = await axiosClient.get<Currency[]>("trading/currencies/")

    return data
  },

  /**
   * GET /api/v1/trading/countries/
   */
  getTradingCountries: async () => {
    const { data } = await axiosClient.get<Country[]>("trading/countries/")

    return data
  },

  /**
   * GET /api/v1/trading/sectors/
   */
  getTradingSectors: async () => {
    const { data } = await axiosClient.get<Sector[]>("trading/sectors/")

    return data
  },

  /**
   * GET /api/v1/trading/companies/
   * Returns light response (no instruments)
   */
  getTradingCompanies: async ({ query }: { query?: string }) => {
    const { data } = await axiosClient.get<TradingCompany[]>("trading/companies/", {
      params: { ticker: query },
    })

    return data
  },

  /**
   * GET /api/v1/trading/companies/${companyId}/
   * Returns full response with instruments and all_related_instruments
   */
  getTradingCompany: async (companyId: number) => {
    const { data } = await axiosClient.get<TradingCompanyDetail>(
      `trading/companies/${companyId}/`,
    )

    return data
  },

  /**
   * PUT /api/v1/trading/companies/${companyId}/
   */
  updateTradingCompany: async ({
    companyId,
    company,
  }: {
    companyId: number
    company: CompanyUpdatePayload
  }) => {
    const { data } = await axiosClient.put<TradingCompanyDetail>(
      `trading/companies/${companyId}/`,
      company,
    )

    return data
  },

  /**
   * GET /api/v1/trading/datasets/${datasetId}/
   */
  getTradingDataset: async (datasetId: string) => {
    const { data } = await axiosClient.get<TradingDataset>(`trading/datasets/${datasetId}/`)
    return data
  },

  /**
   * GET /api/v1/trading/datasets/
   */
  getTradingDatasets: async ({
    pageParam,
    codeStartswithParam,
    nameParam,
    longNameParam,
    sourceParam,
    currencyCodeParam,
    countryCodeParam,
    regionIdParam,
    subRegionIdParam,
    sectorsParam,
    withPaginationParam,
    orderBy,
    sortDirection,
  }: {
    pageParam?: string
    codeStartswithParam?: string
    nameParam?: string
    longNameParam?: string
    sourceParam?: DatasetSource
    currencyCodeParam?: string[]
    countryCodeParam?: string[]
    regionIdParam?: number
    subRegionIdParam?: number
    sectorsParam?: number[]
    withPaginationParam?: boolean
    orderBy?: DatasetOrdering
    sortDirection?: DatasetSorting
  }) => {
    const { data } = await axiosClient.get<{ data: TradingDataset[]; next_cursor: string | null }>(
      "trading/datasets/",
      {
        params: {
          per_page: DATASETS_PER_PAGE,
          next_cursor: pageParam,
          code_startswith: codeStartswithParam,
          name: nameParam,
          long_name: longNameParam,
          source: sourceParam,
          currency_code: currencyCodeParam?.join(","),
          country_code: countryCodeParam?.join(","),
          region_id: regionIdParam,
          sub_region_id: subRegionIdParam,
          sectors: sectorsParam?.join(","),
          with_pagination: withPaginationParam,
          sort_by: orderBy ? orderBy : DatasetOrdering.NAME,
          sort_direction: sortDirection ? sortDirection : DatasetSorting.ASC,
        },
      },
    )

    return data
  },

  /**
   * PUT /api/v1/trading/datasets/${datasetId}/
   */
  updateTradingDataset: async ({
    datasetId,
    dataset,
  }: {
    datasetId: number
    dataset: DatasetUpdatePayload
  }) => {
    const { data } = await axiosClient.put<TradingDataset>(
      `trading/datasets/${datasetId}/`,
      dataset,
    )

    return data
  },

  /**
   * GET /api/v1/trading/regions/
   */
  getTradingRegions: async () => {
    const { data } = await axiosClient.get<Region[]>("trading/regions/")

    return data
  },

  /**
   * GET /api/v1/trading/instruments/
   */
  getTradingInstruments: async ({
    query,
    withPriceUsd,
  }: {
    query: string
    withPriceUsd?: boolean
  }) => {
    const { data } = await axiosClient.get<InstrumentLight[]>("trading/instruments/", {
      params: { ticker: query, with_price_usd: withPriceUsd },
    })

    return data
  },

  /**
   * GET /api/v1/trading/instruments/${instrumentId}/
   */
  getTradingInstrument: async (instrumentId: number) => {
    const { data } = await axiosClient.get<InstrumentDetail>(
      `trading/instruments/${instrumentId}/`,
    )

    return data
  },

  /**
   * PUT /api/v1/trading/instruments/${instrumentId}/
   */
  updateTradingInstrument: async ({
    instrumentId,
    instrument,
  }: {
    instrumentId: number
    instrument: InstrumentUpdatePayload
  }) => {
    const { data } = await axiosClient.put<InstrumentDetail>(
      `trading/instruments/${instrumentId}/`,
      instrument,
    )

    return data
  },

  /**
   * POST /api/v1/trading/datasets/
   */
  createDataset: async (dataset: { name: string; scient_code: string }) => {
    const { data } = await axiosClient.post<TradingDataset>("trading/datasets/", dataset)
    return data
  },

  /**
   * POST /trading/cancel-order/
   * Cancels an order in FlexTrade
   */
  cancelOrder: async ({ orderId }: { orderId: string }) => {
    const { data } = await axiosClient.post("/trading/cancel-order/", { order_id: orderId })
    return data
  },

  /**
   * GET /trading/short-locates/
   * Gets all short locates from FlexTrade
   */
  getShortLocates: async () => {
    const { data } = await axiosClient.get<ShortLocateListResponse>("/trading/short-locates/")
    return data
  },

  /**
   * POST /trading/short-locates/
   * Creates a manual short locate in FlexTrade
   */
  createManualShortLocate: async (payload: ShortLocatePayload) => {
    const { data } = await axiosClient.post<ShortLocateResponse>("/trading/short-locates/", payload)
    return data
  },

  /**
   * GET /trading/datasets/search/
   */
  searchDatasets: async (searchTerm: string) => {
    const { data } = await axiosClient.get<TradingDatasetLight[]>(`/trading/datasets/search/`, {
      params: { q: searchTerm },
    })
    return data
  },

  /**
   * POST /trading/positions/upload/
   * Uploads a CSV file for positions
   */
  uploadPositionsCSV: async (file: File, overwrite?: boolean) => {
    const formData = new FormData()
    formData.append("file", file)
    if (overwrite !== undefined) {
      formData.append("overwrite", overwrite.toString())
    }
    const { data } = await axiosClient.post<CSVUploadResponse>(
      "/trading/positions/upload/",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )
    return data
  },

  /**
   * POST /trading/datasets/upload-csv/
   * Uploads a CSV file for datasets
   */
  uploadDatasetsCSV: async (file: File) => {
    const formData = new FormData()
    formData.append("file", file)
    const { data } = await axiosClient.post<CSVUploadResponse>(
      "/trading/datasets/upload-csv/",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )
    return data
  },
})

export default trading
